import type { ThirdPartyResponseAction } from '@risksmart-app/domain/src/types/consts/third-party-response-action';
import type { UpdateThirdPartyResponseStatusMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateThirdPartyResponseStatusDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateThirdPartyResponseStatusInput {
  Action: ThirdPartyResponseAction;
  ResponseIds: string[];
  Reason?: string;
  RequestType?: string;
  ShareWithRespondents: boolean;
  ThirdPartyId: string;
}

/**
 * Maps the tRPC mutation response to the GraphQL mutation response shape so
 * consumers branching on the result do not need to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): UpdateThirdPartyResponseStatusMutation => ({
  updateThirdPartyResponseStatusAction: {
    __typename: 'GenericMutationOutput',
    affected_rows: data.affected_rows,
  },
});

/**
 * Flag-gated hook for the `updateThirdPartyResponseStatus` mutation
 * (RSP-6891). Defaults to the legacy Apollo mutation and delegates to the tRPC
 * variant when the `trpc` feature flag is enabled.
 */
export const useUpdateThirdPartyResponseStatus = createMutationHook({
  name: 'updateStatus',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.thirdPartyResponse.updateStatus.mutationOptions({
      onSuccess,
    }),
  mapInput: (input: UpdateThirdPartyResponseStatusInput) => input,
  // The reviewer screens read the response through `getById`; the
  // questionnaires tab lists them per third party. Both show Status.
  //
  // CUS-243 also invalidates the conversation family: requesting more
  // information appends to the response's thread, and without this the new
  // entry would not appear in the request history until a manual reload.
  invalidate: (trpc) => [
    trpc.frontend.thirdPartyResponse.pathKey(),
    trpc.frontend.conversation.pathKey(),
  ],
  mapResponse: (result: {
    affected_rows: number;
  }): UpdateThirdPartyResponseStatusMutation =>
    mapTrpcResponseToGraphQL(result),
  graphqlDocument: UpdateThirdPartyResponseStatusDocument,
  graphqlVariables: (input: UpdateThirdPartyResponseStatusInput) => ({
    Action: input.Action,
    ResponseIds: input.ResponseIds,
    Reason: input.Reason,
    RequestType: input.RequestType,
    ShareWithRespondents: input.ShareWithRespondents,
    ThirdPartyId: input.ThirdPartyId,
  }),
});
